/**
 * What the user has selected in a server-paged grid, across every page.
 *
 * Only the rows on the current page are ever in the browser, so "select all" cannot list the rows. It switches to
 * "everything matching the filters", and the rows the user then unticks are remembered as exclusions. Without it the
 * selection is simply the set of ticked rows. Rows are identified by a string key made by the grid.
 */
export class GridSelection {
    private readonly ticked = new Set<string>();
    private readonly excluded = new Set<string>();
    private everythingMatching = false;

    /** True when every row matching the filters is selected, except for the unticked ones. */
    get allMatching(): boolean {
        return this.everythingMatching;
    }

    get tickedKeys(): string[] {
        return Array.from(this.ticked);
    }

    get excludedKeys(): string[] {
        return Array.from(this.excluded);
    }

    isSelected(key: string): boolean {
        return this.everythingMatching ? !this.excluded.has(key) : this.ticked.has(key);
    }

    toggle(key: string): void {
        const set = this.everythingMatching ? this.excluded : this.ticked;
        if (set.has(key)) {
            set.delete(key);
        } else {
            set.add(key);
        }
    }

    /** Selects every row matching the filters, on every page. */
    selectAllMatching(): void {
        this.ticked.clear();
        this.excluded.clear();
        this.everythingMatching = true;
    }

    clear(): void {
        this.ticked.clear();
        this.excluded.clear();
        this.everythingMatching = false;
    }

    /** How many rows are selected, given the number of rows matching the filters. */
    count(totalMatching: number): number {
        return this.everythingMatching ? Math.max(0, totalMatching - this.excluded.size) : this.ticked.size;
    }
}
